import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { ArrowBack, ArrowForward, SyncAlt, Schedule, HelpOutline } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { DetectedConflict } from '@/services/conflictService';
import { buildMatchTimeRefs, generateMatchCardConflictDescription, formatDuration } from '@/utils/conflictFormatter';
import { formatInUserTimezone } from '@/utils/timezoneHelpers';
import { useSubscriptionTier } from '@/hooks/useSubscriptionTier';
import type { ScheduleMatch } from '@/services/scheduleService';
import type { RootState } from '@/store';

interface ConflictPopupContentProps {
  match: ScheduleMatch;
  conflicts: DetectedConflict[];
  /** All conflicts for this date (needed for time disambiguation) */
  dayConflicts: DetectedConflict[];
  isTbd?: boolean;
}

/** Optional travel detail line */
function getTravelDetail(c: DetectedConflict): string | null {
  const { timeConflict } = c;
  if (!timeConflict.travelTime || timeConflict.availableTravelWindow === undefined) return null;
  const travelMin = timeConflict.travelTime.durationSeconds / 60;
  const windowMin = Math.max(0, timeConflict.availableTravelWindow);
  const buffer = windowMin - travelMin;
  if (buffer > 0) {
    return `~${formatDuration(travelMin)} drive, ${formatDuration(buffer)} to spare`;
  }
  return `~${formatDuration(travelMin)} drive`;
}

/** Get the direction icon component for the conflict ribbon */
function getDirectionIcon(info: { direction?: 'incoming' | 'outgoing'; icon?: string }) {
  if (info.direction === 'incoming') return ArrowBack;
  if (info.direction === 'outgoing') return ArrowForward;
  if (info.icon === 'overlap' || info.icon === 'schedule') return SyncAlt;
  if (info.icon === 'tbd') return Schedule;
  return HelpOutline;
}

/** Build header parts: { title, subtitle } */
function buildHeader(match: ScheduleMatch, formatTime: (dt: string | null) => string): { title: string; subtitle: string } {
  const playerFirstNames = match.players?.map(p => p.name.split(' ')[0]) || [];
  const nameStr = playerFirstNames.length > 0 ? playerFirstNames.join(' & ') : '';
  const time = formatTime(match.dateTime);

  // Figure out the opponent — use the first player's perspective
  let opponent = '';
  if (match.players && match.players.length > 0) {
    const p = match.players[0];
    opponent = p.isHome ? match.awayTeam : match.homeTeam;
  } else {
    opponent = `${match.homeTeam} vs ${match.awayTeam}`;
  }

  const title = nameStr
    ? `Conflicts for ${nameStr}'s ${time} match`
    : `Conflicts for ${time} match`;

  return { title, subtitle: `v ${opponent}` };
}

const SEVERITY_COLORS = {
  light: { DIRECT: '#dc2626', MODERATE: '#d97706', MINIMAL: '#2563eb' },
  dark: { DIRECT: '#ef4444', MODERATE: '#f59e0b', MINIMAL: '#60a5fa' },
};

const SEVERITY_BORDER = {
  light: { DIRECT: '#fecaca', MODERATE: '#fde68a', MINIMAL: '#bfdbfe' },
  dark: { DIRECT: 'rgba(239,68,68,0.3)', MODERATE: 'rgba(245,158,11,0.3)', MINIMAL: 'rgba(96,165,250,0.3)' },
};

const SEVERITY_BG = {
  light: { DIRECT: 'rgba(220,38,38,0.06)', MODERATE: 'rgba(217,119,6,0.06)', MINIMAL: 'rgba(37,99,235,0.06)' },
  dark: { DIRECT: 'rgba(239,68,68,0.08)', MODERATE: 'rgba(245,158,11,0.08)', MINIMAL: 'rgba(96,165,250,0.08)' },
};

const ConflictPopupContent: React.FC<ConflictPopupContentProps> = ({ match, conflicts, dayConflicts, isTbd }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = isDark ? SEVERITY_COLORS.dark : SEVERITY_COLORS.light;
  const borders = isDark ? SEVERITY_BORDER.dark : SEVERITY_BORDER.light;
  const bgs = isDark ? SEVERITY_BG.dark : SEVERITY_BG.light;
  const { hasFeature } = useSubscriptionTier();
  const showTravelDetails = hasFeature('travel_aware_conflicts');
  const distanceUnit = useSelector((state: RootState) => state.auth.user?.distanceUnit) || 'MILES';

  const timeRefs = buildMatchTimeRefs(dayConflicts);

  const formatTime = (dateTime: string | null) => {
    if (!dateTime) return 'TBD';
    return formatInUserTimezone(dateTime, 'h:mm A', false);
  };

  // TBD-only message
  if (isTbd && conflicts.length === 0) {
    return (
      <Box>
        <Box sx={{ px: '18px', pt: '16px', pb: '12px' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.secondary', mb: 1, pr: 4 }}>Schedule Conflicts</Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', lineHeight: 1.4, pr: 4 }}>{buildHeader(match, formatTime).title}</Typography>
          <Typography sx={{ fontSize: '14px', color: 'text.secondary', mt: '2px' }}>{buildHeader(match, formatTime).subtitle}</Typography>
        </Box>
        <Box sx={{ px: '18px', py: '16px' }}>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: '10px 12px' }}>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              This match hasn't been scheduled yet. Once a time is set, we'll check for conflicts.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ px: '18px', pt: '16px', pb: '12px' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', mb: 0.5, pr: 4 }}>Schedule Conflicts</Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', lineHeight: 1.4, pr: 4 }}>
          {buildHeader(match, formatTime).title}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: 'text.secondary', mt: '2px' }}>
          {buildHeader(match, formatTime).subtitle}
        </Typography>
      </Box>

      <Box sx={{ px: '18px', py: '14px' }}>
        {conflicts.map((c, i) => {
          const info = generateMatchCardConflictDescription(c, match.id, timeRefs);
          const travelDetail = showTravelDetails ? getTravelDetail(c) : null;
          const isMatch1 = c.match1.match.id === match.id;
          const otherSide = isMatch1 ? c.match2 : c.match1;
          const otherMatch = otherSide.match;
          const severity = c.severity;
          const severityColor = colors[severity];
          const severityBorder = borders[severity];
          const severityBg = bgs[severity];

          const DirectionIcon = getDirectionIcon(info);

          const formatDistance = (meters?: number) => {
            if (!meters) return null;
            if (distanceUnit === 'KILOMETERS') {
              const km = meters / 1000;
              return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
            }
            const miles = meters / 1609.34;
            return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
          };

          const distanceStr = c.timeConflict.travelTime
            ? formatDistance(c.timeConflict.travelTime.distanceMeters)
            : null;

          return (
            <Box key={c.id} sx={{ mb: i < conflicts.length - 1 ? '14px' : 0 }}>
              {/* Match details card with description inside */}
              <Box sx={{
                border: '0.5px solid',
                borderColor: severityBorder,
                borderLeft: `3px solid ${severityColor}`,
                borderRadius: '10px',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}>
                {/* Direction ribbon — short label + icon */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  px: '12px',
                  py: '4px',
                  bgcolor: severityBg,
                  borderBottom: '0.5px solid',
                  borderColor: severityBorder,
                }}>
                  <DirectionIcon sx={{ fontSize: 15, color: severityColor, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: severityColor }}>
                    {formatTime(otherMatch.dateTime)}
                  </Typography>
                  {otherSide.playerName && (
                    <>
                      <Typography component="span" sx={{ fontSize: '13px', color: severityColor, opacity: 0.7 }}>·</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: severityColor }}>
                        {otherSide.playerName}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Card body */}
                <Box sx={{ p: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {/* Description */}
                  <Typography sx={{ fontSize: '13px', color: severityColor, fontWeight: 500 }}>
                    {info.text}
                  </Typography>

                  {/* Teams */}
                  <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                    {otherMatch.homeTeam} vs {otherMatch.awayTeam}
                  </Typography>

                  {/* Divider */}
                  {otherMatch.location && <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}

                  {/* Venue */}
                  {otherMatch.location && (
                    <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                      {otherMatch.location}
                    </Typography>
                  )}

                  {/* Travel detail + distance */}
                  {(travelDetail || distanceStr) && (
                    <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                      {travelDetail}{travelDetail && distanceStr && ' · '}{distanceStr}
                    </Typography>
                  )}

                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ConflictPopupContent;
